/**
 * Payment Method Form Component
 * Uses Stripe Elements to collect card details and add payment method
 */

import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import { useApi } from '../../../hooks/useApi';
import { useStripeContext } from '../../../components/common/StripeProvider';

// Card Element styling for dark theme
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: '"Roboto", sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.5)',
      },
      iconColor: '#1976d2',
    },
    invalid: {
      color: '#f44336',
      iconColor: '#f44336',
    },
  },
  hidePostalCode: false,
};

/**
 * PaymentMethodForm Component
 *
 * @param {Object} props
 * @param {Function} props.onSuccess - Callback when payment method is added successfully
 * @param {Function} props.onError - Callback when an error occurs
 * @param {string} props.clientSecret - The SetupIntent client secret (optional, can come from context)
 * @param {string} props.submitLabel - Label for the submit button
 * @param {boolean} props.showSecurityNote - Whether to show security note
 */
function PaymentMethodForm({
  onSuccess,
  onError,
  clientSecret: propClientSecret,
  submitLabel = 'Add Payment Method',
  showSecurityNote = true,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const api = useApi();
  const stripeContext = useStripeContext();

  // Use clientSecret from prop or context
  const clientSecret = propClientSecret || stripeContext?.clientSecret;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not ready. Please wait...');
      return;
    }

    if (!cardComplete) {
      setError('Please complete the card details');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      // Confirm the SetupIntent with the card details
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (setupIntent.status !== 'succeeded') {
        throw new Error('Payment setup did not complete. Please try again.');
      }

      // Confirm with our backend
      const confirmResult = await api.post('/api/billing/confirm-setup', {
        setupIntentId: setupIntent.id,
      });

      // Success!
      if (onSuccess) {
        onSuccess({
          setupIntentId: setupIntent.id,
          paymentMethod: confirmResult.paymentMethod,
        });
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to add payment method';
      setError(errorMessage);
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading if clientSecret is not yet available
  if (!clientSecret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Preparing payment form...
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Card Input */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: "text.secondary",
            mb: 1
          }}>
          Card Details
        </Typography>
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: error ? 'error.main' : 'rgba(255, 255, 255, 0.23)',
            borderRadius: 1,
            bgcolor: 'background.paper',
            transition: 'border-color 0.2s',
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: '0 0 0 1px #1976d2',
            },
          }}
        >
          <CardElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
        </Box>
      </Box>
      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {/* Security Note */}
      {showSecurityNote && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <LockIcon fontSize="small" color="success" />
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            Your card details are securely processed by Stripe. We never store your full card number.
          </Typography>
        </Box>
      )}
      {/* Submit Button */}
      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!stripe || !cardComplete || loading}
        startIcon={loading ? <CircularProgress size={20} /> : <CreditCardIcon />}
      >
        {loading ? 'Processing...' : submitLabel}
      </Button>
    </Box>
  );
}

export default PaymentMethodForm;
